import React, { useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import { toast } from "react-toastify";
import {
  getContractorProposal,
  getProjectBillingMilestones,
} from "../../services/contractorService";
import { renderCurrencyIcon } from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import Overview from "./components/Overview";
import BillingMilestones from "./components/BillingMilestones";
import CustomerPayments from "./components/CustomerPayments";
import Settlements from "./components/Settlements";
import Contract from "./components/Contract";
import Changes from "./components/Changes";
import WorkInProgress from "./components/WorkInProgress";
import ProjectLogIssues from "../NewProjectDetails/components/ProjectLogIssues";
import { statusStyleArray } from "../NewProjectDetails/config";
import { getProjectsListPath } from "../Projects/utils/projectViewHelpers";

const DEFAULT_PROJECT_IMAGE =
  "https://renohome.blob.core.windows.net/project-images/5b501197-5ea2-44a6-866c-a149461cb322";

const TABS = [
  { id: "overview", label: "Overview", route: "" },
  {
    id: "billing-milestones",
    label: "Billing Milestones",
    route: "billing-milestones",
  },
  {
    id: "customer-payments",
    label: "Customer Payments",
    route: "customer-payments",
  },
  { id: "settlements", label: "Settlements", route: "settlements" },
  { id: "contract", label: "Contract", route: "contract" },
  { id: "changes", label: "Changes", route: "changes" },
  { id: "issues", label: "Issue", route: "issues" },
  { id: "dispute", label: "Dispute", route: "dispute" },
];

const StyledTab = styled(Tab)(({ selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d !important" : "#fff !important",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
}));

const formatDateLabel = (val) => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return val;
  }
};

const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text || "";
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

const getApiStatusCode = (res) => {
  const code = res?.statusCode ?? res?.status_code ?? res?.code;
  if (code == null) return null;
  const parsed = Number(code);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function ContractorProjectDetails() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  // Project-level billing milestones (with nested delivery_approvals[] and
  // delivery_memos[]). Fetched separately from the proposal because the
  // proposal endpoint doesn't carry approval state.
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);

  const ensureSuccess = React.useCallback((res, fallbackMessage) => {
    const statusCode = getApiStatusCode(res);
    if (statusCode != null && statusCode !== 2000) {
      toast.error(res?.message || fallbackMessage || "Something went wrong");
      return false;
    }
    return true;
  }, []);

  // Project id used for backend lookups. Prefer the explicit ?project_id
  // query param when present; fall back to the route segment so legacy
  // links keep working.
  const projectId = searchParams.get("project_id") || id;
  const proposalId = id;
  // Drives stage-aware UI on the Customer Payments + Settlements tabs and
  // gates Send for Approval / Request Payment CTAs on Billing Milestones.
  // Falls back to PROPOSAL until the proposal payload arrives so we don't
  // flash project-stage UI on first paint.
  const stage = proposal?.stage
    ? String(proposal.stage).toUpperCase()
    : "";

  // Dispute, Changes and Issue tabs are only meaningful once the proposal
  // has been promoted to a PROJECT — hide them on earlier stages.
  const visibleTabs = useMemo(() => {
    if (stage === "PROJECT") return TABS;
    const hidden = new Set(["dispute", "changes", "issues"]);
    return TABS.filter((t) => !hidden.has(t.id));
  }, [stage]);

  // Contractor-side activity options for the Issue Log modal: flatten the
  // deliverables array from each billing milestone (skipping any milestone
  // that has none) and surface deliverable_description as the option label.
  // We keep `id` unique per row (composite "<milestoneId>-<idx>") and
  // forward the parent milestone id separately so the payload still ends
  // up as `activity: { milestoneId }`.
  const contractorIssueActivities = useMemo(() => {
    const out = [];
    (Array.isArray(projectMilestones) ? projectMilestones : []).forEach((m) => {
      const dels = Array.isArray(m?.deliverables) ? m.deliverables : [];
      if (dels.length === 0) return;
      dels.forEach((d, idx) => {
        const desc = d?.deliverable_description;
        if (!desc) return;
        out.push({
          id: `${m.id}-${idx}`,
          milestone_id: m.id,
          milestone_name: desc,
        });
      });
    });
    return out;
  }, [projectMilestones]);

  const tabIndex = useMemo(() => {
    const found = visibleTabs.findIndex((t) => t.route === (tab || ""));
    return found === -1 ? 0 : found;
  }, [tab, visibleTabs]);

  // Refetch the proposal whenever the active tab changes so each tab sees
  // a fresh `stage` value — REQUEST → PROPOSAL → PROJECT transitions can
  // happen out-of-band on the backend, and tab-level UI (Customer Payments
  // / Settlements / Billing Milestones CTAs) keys off it. The first load
  // shows a spinner; subsequent tab-driven refetches refresh silently.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const isInitialLoad = proposal === null;
    (async () => {
      if (isInitialLoad) setLoading(true);
      try {
        const res = await getContractorProposal(id);
        if (!ensureSuccess(res, "Failed to load proposal")) return;
        const data = res?.data || res;
        if (!cancelled) setProposal(data || null);
      } catch (e) {
        console.error("Failed to load contractor proposal", e);
      } finally {
        if (!cancelled && isInitialLoad) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tab, ensureSuccess]);

  // Refetchable so the BillingMilestones tab can refresh after the user
  // returns from the Request for Approval flow.
  const reloadMilestones = React.useCallback(async () => {
    if (!projectId) return;
    setMilestonesLoading(true);
    try {
      const res = await getProjectBillingMilestones(projectId);
      if (!ensureSuccess(res, "Failed to load billing milestones")) {
        setProjectMilestones([]);
        return;
      }
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];
      setProjectMilestones(list);
    } catch (e) {
      console.error("Failed to load project billing milestones", e);
      setProjectMilestones([]);
    } finally {
      setMilestonesLoading(false);
    }
  }, [projectId, ensureSuccess]);

  useEffect(() => {
    reloadMilestones();
  }, [reloadMilestones]);

  const handleTabChange = (_, newValue) => {
    const route = visibleTabs[newValue]?.route;
    const base = route
      ? `/project/details/${id}/${route}`
      : `/project/details/${id}`;
    // Always preserve the actual project id (from the proposal payload, or
    // fall back to whatever came in via the query param / route). The path
    // segment carries the proposal id; the project_id query param carries
    // the project id and feeds /api/v2/payout/project/:id etc.
    const resolvedProjectId =
      proposal?.project_id || proposal?.projectId || projectId || id;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(resolvedProjectId),
    });
    // Preserve the "from" hint across tab switches so the back arrow
    // keeps routing to the correct origin (e.g. /dashboard) even after
    // the user clicks around between tabs.
    const fromParam = searchParams.get("from");
    if (fromParam) qs.set("from", fromParam);
    const viewParam = searchParams.get("view");
    if (viewParam) qs.set("view", viewParam);
    navigate(`${base}?${qs.toString()}`);
  };

  const projectImage =
    (typeof proposal?.image_url === "string" && proposal.image_url) ||
    DEFAULT_PROJECT_IMAGE;

  const status = proposal?.proposal_status || proposal?.status || "draft";

  const renderTab = () => {
    const activeTabId = visibleTabs[tabIndex]?.id;
    switch (activeTabId) {
      case "overview":
        return <Overview proposal={proposal} />;
      case "billing-milestones":
        return (
          <BillingMilestones
            milestones={projectMilestones}
            loading={milestonesLoading}
            onReload={reloadMilestones}
            projectId={projectId}
            proposalId={proposalId}
            stage={stage}
          />
        );
      case "customer-payments":
        return (
          <CustomerPayments
            projectId={projectId}
            proposalId={proposalId}
            stage={stage}
          />
        );
      case "settlements":
        return (
          <Settlements
            projectId={projectId}
            proposalId={proposalId}
            stage={stage}
          />
        );
      case "contract":
        return <Contract proposal={proposal} />;
      case "changes":
        // return <WorkInProgress label="Changes" />;
     return <Changes loading={milestonesLoading} />;
      case "issues":
        return (
          <ProjectLogIssues
            projectId={projectId}
            projectDetails={null}
            isMobile={false}
            roomAsText
            activitiesOverride={contractorIssueActivities}
            sourceTypeOverride="CONTRACTOR"
            omitActivity
          />
        );
      case "dispute":
        return <WorkInProgress label="Dispute" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        fontFamily: "Inter Tight",
      }}
    >
      <Box
        sx={{
          padding: { xs: "4vh 5vw 2vh", md: "4vh 2vw 2vh" },
          background:
            "linear-gradient(180deg, rgba(255, 162, 162, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "30px",
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            onClick={() =>
              navigate(
                searchParams.get("from") === "projects"
                  ? getProjectsListPath(searchParams.get("view"))
                  : "/contractor/proposals"
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(
                  searchParams.get("from") === "projects"
                    ? getProjectsListPath(searchParams.get("view"))
                    : "/contractor/proposals"
                );
              }
            }}
            sx={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LeftArrowIcon />
          </Box>
          <Typography sx={{ fontSize: "12px", color: "#0D0D0E" }}>
            <span style={{ color: "#949DAA", fontSize: "14px" }}>ID:</span>{" "}
            #{proposal?.proposal_id || id}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: "15px",
                height: "15px",
                borderRadius: "50%",
                backgroundColor:
                  statusStyleArray.find((s) => s.status === status)
                    ?.backgroundColor || "#FFAD5C",
                border: "3px solid #e5e5e5",
              }}
            />
            <Typography
              sx={{ fontSize: "14px", marginLeft: "8px", color: "#0D0D0E" }}
            >
              {getStatusDisplayText(status)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: { xs: "1rem 2rem", md: "2vh 2vw" },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "2rem", md: 0 },
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", gap: "15px", flex: 2, alignItems: "center" }}>
            <Box
              component="img"
              src={projectImage}
              onError={(e) => {
                e.currentTarget.src = DEFAULT_PROJECT_IMAGE;
              }}
              alt="Project"
              sx={{
                width: 90,
                height: 90,
                borderRadius: "16px",
                objectFit: "cover",
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography
                sx={{
                  color: "#0D0D0E",
                  fontSize: { xs: "15px", md: "22px" },
                  fontWeight: 500,
                }}
              >
                {proposal?.project_name || "Untitled Proposal"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
            <Typography sx={{ color: "#949DAA", fontSize: "14px" }}>
              {stage === "PROJECT" ? "Project Value" : "Proposal Value"}
            </Typography>
            <Typography
              sx={{
                color: "#0D0D0E",
                fontSize: "18px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              {renderCurrencyIcon("black-small")}
              {formattedAmountWithoutAed(
                proposal?.value || proposal?.proposal_value || 0
              )}
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tabIndex}
          variant="scrollable"
          scrollButtons={false}
          onChange={handleTabChange}
          sx={{
            marginTop: "3vh",
            flexShrink: 0,
            "& .MuiTabs-indicator": { background: "none" },
            "& .MuiTabs-scroller": { scrollbarWidth: "none" },
            "& .MuiTabs-scroller::-webkit-scrollbar": { display: "none" },
          }}
        >
          {visibleTabs.map((t, i) => (
            <StyledTab key={t.id} label={t.label} selected={tabIndex === i} />
          ))}
        </Tabs>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            marginTop: "1vh",
          }}
        >
          {renderTab()}
        </Box>
      </Box>
    </Box>
  );
}
